import {
  Row,
  Col,
  Input,
  InputNumber,
  Select,
  AutoComplete,
  Checkbox,
} from 'antd';

import { Form, WrapperSelect } from 'common/components';

import { useGetSupplementPartOf } from '../../hook/useGetSupplementPartOf';
import { useFactPanelQasRequiredFields } from '../../hook/useFactPanelQasRequiredFields';
import { useGetNutritionEnum } from '../../hook/useNutritionLabel';

import {
  DAILY_SYMBOL_SUGGESTIONS,
  SUPPLEMENT_NUTRIENT_UOM,
} from '../../constants';

import Messages from 'i18n/messages/product';
import { useIntl } from 'react-intl';
import { sortBy } from 'lodash';
import { QaSpecHelpContentWithoutLabel } from 'pages/qa-spec/components/shared/QaSpecHelpContent';
import { TOOlTIP_FIELDS } from 'static/tooltipFields';

const formItemStyle = { marginBottom: 4 };

const SupplementIngredientForm = (props) => {
  const intl = useIntl();

  const { fieldName, namePath, productId, typeView, children } = props;

  const { partOfList } = useGetSupplementPartOf({ fieldName, namePath });

  const { getRequiredRule } = useFactPanelQasRequiredFields({
    productId,
    typeView,
    section: 'qaSpecification.factsPanel.supplementLabel',
  });

  const { measurementPrecisionCodeEnum } = useGetNutritionEnum(); // Fetch precision options

  const isQaSpecType = typeView === 'qa-spec';

  return (
    <div className='supplement-form__add-nutrient-form'>
      <Form.Item
        name={[...namePath, 'ingredientName']}
        rules={[
          {
            required: true,
            message: 'Name is required',
          },
        ]}
        style={formItemStyle}
      >
        <QaSpecHelpContentWithoutLabel
          fieldPath={TOOlTIP_FIELDS.dietaryIngredientName.fieldPath}
          isEnabled={isQaSpecType}
        >
          <Input.TextArea
            placeholder={intl.formatMessage(Messages.supplementIngredientName)}
          />
        </QaSpecHelpContentWithoutLabel>
      </Form.Item>
      <Form.Item
        name={[...namePath, 'ingredientsPartOf']}
        style={formItemStyle}
      >
        <QaSpecHelpContentWithoutLabel
          fieldPath={TOOlTIP_FIELDS.dietaryIngredientsPartOf.fieldPath}
          isEnabled={isQaSpecType}
        >
          <Select
            allowClear
            placeholder={intl.formatMessage(Messages.supplementPartOf)}
            getPopupContainer={(triggerNode) => {
              return triggerNode;
            }}
          >
            {partOfList.map((partOfItem) => {
              return (
                <Select.Option key={partOfItem} value={partOfItem}>
                  {partOfItem}
                </Select.Option>
              );
            })}
          </Select>
        </QaSpecHelpContentWithoutLabel>
      </Form.Item>

      <Form.Item
        name={[...namePath, 'descriptor']}
        style={formItemStyle}
        rules={[
          getRequiredRule({
            fieldName: ['dietaryIngredients', 'descriptor'],
            displayName: intl.formatMessage(Messages.supplementDescriptor),
          }),
        ]}
      >
        <QaSpecHelpContentWithoutLabel
          fieldPath={TOOlTIP_FIELDS.dietaryDescriptor.fieldPath}
          isEnabled={isQaSpecType}
        >
          <Input.TextArea
            placeholder={intl.formatMessage(Messages.supplementDescriptor)}
          />
        </QaSpecHelpContentWithoutLabel>
      </Form.Item>

      {/* New Quantity Precision Dropdown */}
      <Form.Item
        name={[...namePath, 'quantityPrecision']}
        style={formItemStyle}
        initialValue='Exact' // Default to "Exact"
      >
        <QaSpecHelpContentWithoutLabel
          fieldPath={TOOlTIP_FIELDS.dietaryQuantityPrecision.fieldPath}
          isEnabled={isQaSpecType}
        >
          <WrapperSelect
            placeholder='Select Quantity Precision'
            showSearch
            filterOption={(input, option) =>
              option?.value?.toLowerCase().indexOf(input?.toLowerCase()) >= 0
            }
            getPopupContainer={(triggerNode) => triggerNode}
          >
            {sortBy(measurementPrecisionCodeEnum?.[0]?.enumProperties, [
              'enumDisplayName',
              'enumGroup',
            ])?.map((item, index) => (
              <Select.Option value={item.enumDisplayName} key={index}>
                {item.enumDisplayName}
              </Select.Option>
            ))}
          </WrapperSelect>
        </QaSpecHelpContentWithoutLabel>
      </Form.Item>

      <Row wrap={false}>
        <Col span={14}>
          <Form.Item
            name={[...namePath, 'quantity']}
            style={formItemStyle}
            rules={[
              getRequiredRule({
                fieldName: ['dietaryIngredients', 'quantity'],
                displayName: intl.formatMessage(Messages.supplementQuantity),
              }),
            ]}
          >
            <QaSpecHelpContentWithoutLabel
              fieldPath={TOOlTIP_FIELDS.dietaryQuantity.fieldPath}
              isEnabled={isQaSpecType}
            >
              <InputNumber
                min={0}
                placeholder={intl.formatMessage(Messages.supplementQuantity)}
              />
            </QaSpecHelpContentWithoutLabel>
          </Form.Item>
        </Col>
        <Col span={10}>
          <Form.Item
            name={[...namePath, 'quantityUom']}
            style={formItemStyle}
            rules={[
              getRequiredRule({
                fieldName: ['dietaryIngredients', 'quantityUom'],
                displayName: 'Unit',
              }),
            ]}
          >
            <QaSpecHelpContentWithoutLabel
              fieldPath={TOOlTIP_FIELDS.dietaryQuantityUom.fieldPath}
              isEnabled={isQaSpecType}
            >
              <Select
                allowClear
                getPopupContainer={(triggerNode) => {
                  return triggerNode;
                }}
              >
                {SUPPLEMENT_NUTRIENT_UOM.map((UNIT) => {
                  return (
                    <Select.Option key={UNIT} value={UNIT}>
                      {UNIT}
                    </Select.Option>
                  );
                })}
              </Select>
            </QaSpecHelpContentWithoutLabel>
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        name={[...namePath, 'quantityDescription']}
        style={formItemStyle}
        rules={[
          getRequiredRule({
            fieldName: ['dietaryIngredients', 'quantityDescription'],
            displayName: intl.formatMessage(
              Messages.supplementQuantityDescription
            ),
          }),
        ]}
      >
        <QaSpecHelpContentWithoutLabel
          fieldPath={TOOlTIP_FIELDS.dietaryQuantityDescription.fieldPath}
          isEnabled={isQaSpecType}
        >
          <Input
            placeholder={intl.formatMessage(
              Messages.supplementQuantityDescription
            )}
          />
        </QaSpecHelpContentWithoutLabel>
      </Form.Item>

      <Form.Item
        name={[...namePath, 'dailySymbol']}
        rules={[
          getRequiredRule({
            fieldName: ['dietaryIngredients', 'dailySymbol'],
            displayName: intl.formatMessage(Messages.supplementDailySymbol),
          }),
        ]}
      >
        <QaSpecHelpContentWithoutLabel
          fieldPath={TOOlTIP_FIELDS.dietaryDailySymbol.fieldPath}
          isEnabled={isQaSpecType}
        >
          <AutoComplete
            allowClear
            options={DAILY_SYMBOL_SUGGESTIONS}
            placeholder={intl.formatMessage(Messages.supplementDailySymbol)}
            getPopupContainer={(triggerNode) => {
              return triggerNode;
            }}
          />
        </QaSpecHelpContentWithoutLabel>
      </Form.Item>
      <Row>
        <Col span={8}>
          <Form.Item
            name={[...namePath, 'italicDietary']}
            valuePropName='checked'
            style={formItemStyle}
            rules={[
              getRequiredRule({
                fieldName: ['dietaryIngredients', 'italicDietary'],
                displayName: intl.formatMessage(Messages.supplementItalicName),
              }),
            ]}
          >
            <QaSpecHelpContentWithoutLabel
              fieldPath={TOOlTIP_FIELDS.dietaryItalicDietary.fieldPath}
              isEnabled={isQaSpecType}
            >
              <Checkbox>
                {intl.formatMessage(Messages.supplementItalicName)}
              </Checkbox>
            </QaSpecHelpContentWithoutLabel>
          </Form.Item>
        </Col>
        <Col span={16}>
          <Form.Item
            name={[...namePath, 'italicDescriptor']}
            valuePropName='checked'
            style={formItemStyle}
            rules={[
              getRequiredRule({
                fieldName: ['dietaryIngredients', 'italicDescriptor'],
                displayName: intl.formatMessage(
                  Messages.supplementItalicDescriptor
                ),
              }),
            ]}
          >
            <QaSpecHelpContentWithoutLabel
              fieldPath={TOOlTIP_FIELDS.dietaryItalicDescriptor.fieldPath}
              isEnabled={isQaSpecType}
            >
              <Checkbox>
                {intl.formatMessage(Messages.supplementItalicDescriptor)}
              </Checkbox>
            </QaSpecHelpContentWithoutLabel>
          </Form.Item>
        </Col>
      </Row>
      {children}
    </div>
  );
};

export default SupplementIngredientForm;
